import { ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';
import { verificationPageUrl } from '@overlayqa/shared';
import { ModalPortal } from '@/components/shared/ModalPortal';

export interface BulkVerifyDialogIssue {
  id: string;
  title: string;
  pageUrl: string | null;
}

export interface BulkVerifyDialogProps {
  issues: BulkVerifyDialogIssue[];
  onCancel: () => void;
  onStart: () => void;
  loading?: boolean;
}

export function BulkVerifyDialog({ issues, onCancel, onStart, loading }: BulkVerifyDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  const { groups, eligible, excluded } = useMemo(() => {
    const groupMap = new Map<string, number>();
    let excludedCount = 0;
    for (const i of issues) {
      if (!i.pageUrl) {
        excludedCount += 1;
        continue;
      }
      try {
        const u = verificationPageUrl(i.pageUrl);
        groupMap.set(u, (groupMap.get(u) ?? 0) + 1);
      } catch {
        excludedCount += 1;
      }
    }
    const eligibleCount = issues.length - excludedCount;
    return { groups: groupMap, eligible: eligibleCount, excluded: excludedCount };
  }, [issues]);

  // ~30s per verification (capture + verify + AI). Round up.
  const estimateMin = Math.max(1, Math.ceil((eligible * 30) / 60));

  const dialogRef = useRef<HTMLDivElement>(null);
  const dismiss = useCallback(() => {
    if (!loading) onCancel();
  }, [loading, onCancel]);
  useModalA11y(true, dialogRef, dismiss);

  // All actions are disabled during startup. Keep keyboard focus inside the
  // dialog until the request settles, then restore its normal Cancel action.
  useEffect(() => {
    if (loading) dialogRef.current?.focus();
    else cancelRef.current?.focus();
  }, [loading]);

  const overlay = (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      role="dialog"
      tabIndex={-1}
      aria-modal="true"
      aria-busy={loading || undefined}
      aria-labelledby="bulk-verify-title"
      onKeyDownCapture={(e) => {
        if (loading && e.key === 'Tab') e.preventDefault();
      }}
      onClick={(e) => {
        // Close on backdrop click only (not on inner click)
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
          <ModalTitle icon={CheckmarkCircle01Icon} id="bulk-verify-title" className="oq-dialog__title">
              Verify {eligible} {eligible === 1 ? 'fix' : 'fixes'}
            </ModalTitle>
          <button
            type="button"
            onClick={dismiss}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pt-4 pb-5">
          {groups.size > 0 ? (
            <p className="text-sm text-gray-600 mb-4">
              Grouped across {groups.size} {groups.size === 1 ? 'page' : 'pages'}:
            </p>
          ) : (
            <p className="text-sm text-gray-600 mb-4">No eligible issues to verify.</p>
          )}

          {groups.size > 0 && (
            // A headed table (DataTable's header classes), so the count column
            // has a name; it was a bare <ul> until 2026-09-09.
            <div className="mb-4 max-h-64 overflow-y-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm table-fixed">
                <thead className="bg-gray-50/50 border-b border-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Page</th>
                    <th className="w-24 px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Issues</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Array.from(groups.entries()).map(([url, count]) => {
                    let pathname = '/';
                    try {
                      pathname = new URL(url).pathname || '/';
                    } catch {
                      pathname = url;
                    }
                    return (
                      <tr key={url}>
                        <td className="px-3 py-2 truncate">
                          <code className="text-gray-700" title={url}>{pathname}</code>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-500">{count} {count === 1 ? 'issue' : 'issues'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {excluded > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 mb-4">
              {excluded} {excluded === 1 ? 'issue is' : 'issues are'} excluded from this run because they have no captured page URL.
            </p>
          )}

          <p className="text-sm text-gray-500 mb-2">
            Verification runs in your browser. Open each page when prompted.
          </p>
          <p className="text-sm text-gray-500 mb-5">
            Estimated time: ~{estimateMin} {estimateMin === 1 ? 'minute' : 'minutes'}.
          </p>
          <div className="flex justify-end gap-3">
            <button
              ref={cancelRef}
              type="button"
              className="btn-secondary"
              onClick={dismiss}
              disabled={loading}
            ><ModalAction kind="cancel">
              Cancel
            </ModalAction></button>
            <button
              type="button"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onStart}
              disabled={loading || eligible === 0}
            ><ModalAction kind="verify">
              {loading ? 'Starting...' : 'Start verify'}
            </ModalAction></button>
          </div>
        </div>
      </div>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}
